import { hasAnyRole } from '@lucid/auth';

/**
 * Feature flag configuration for page visibility
 *
 * Uses VITE_ENABLED_PAGES environment variable to control which pages are visible.
 * This is a whitelist approach - only pages listed in the env var will be accessible.
 *
 * @example
 * // .env.development
 * VITE_ENABLED_PAGES=dashboard,prospectDesk,orderHub,vehicleHub
 *
 * // .env.production
 * VITE_ENABLED_PAGES=dashboard,prospectDesk,orderHub
 */

/**
 * Available page identifiers for feature flagging
 */
export type PageId =
  | 'dashboard'
  | 'inbox'
  | 'notifications'
  | 'trackOrders'
  | 'admin'
  | 'prospectDesk'
  | 'orderHub'
  | 'partsStore'
  | 'serviceCenter'
  | 'claimsCenter'
  | 'vehicleHub'
  | 'customerCare'
  | 'registrationDesk'
  | 'accountsDesk';

/**
 * Parse enabled pages from environment variable
 */
const getEnabledPages = (): Set<string> => {
  const enabledPagesEnv = import.meta.env.VITE_ENABLED_PAGES || '';
  const pages = enabledPagesEnv
    .split(',')
    .map((page: string) => page.trim().toLowerCase())
    .filter(Boolean);
  return new Set(pages);
};

const enabledPages = getEnabledPages();

/**
 * Check if a page is enabled based on VITE_ENABLED_PAGES environment variable
 * @param pageId - The page identifier to check
 * @returns true if the page is enabled, false otherwise
 * @example
 * ```ts
 * if (isPageEnabled('claimsCenter')) {
 *   // Render claims center routes
 * }
 * ```
 */
export const isPageEnabled = (pageId: PageId): boolean => {
  // If no pages specified, enable all (for backwards compatibility)
  if (enabledPages.size === 0) {
    return true;
  }
  return enabledPages.has(pageId.toLowerCase());
};

/**
 * Filter an array of routes based on enabled pages
 * @param routes - Array of route objects with pageId property
 * @returns Filtered array of routes that are enabled
 */
export const filterEnabledRoutes = <T extends { pageId?: PageId }>(routes: T[]): T[] => {
  return routes.filter((route) => !route.pageId || isPageEnabled(route.pageId));
};

/**
 * Maps sidebar navigation IDs (kebab-case) to PageId (camelCase)
 */
const sidebarIdToPageId: Record<string, PageId> = {
  'admin-panel': 'admin',
  'prospect-desk': 'prospectDesk',
  'order-hub': 'orderHub',
  'parts-store': 'partsStore',
  'service-center': 'serviceCenter',
  'claims-center': 'claimsCenter',
  'vehicle-hub': 'vehicleHub',
  'customer-care': 'customerCare',
  'registration-desk': 'registrationDesk',
  'accounts-desk': 'accountsDesk',
};

/** Role that bypasses all role-based restrictions */
const LUCID_ADMIN_ROLE = 'Lucid Admin';

/**
 * Maps navigation IDs to the roles required to view them.
 * Items not listed here have no role restriction.
 */
const navigationRoleRequirements: Record<string, string[]> = {
  'admin-panel': ['Agency-Admin-R4', 'Importer-Admin-R1', LUCID_ADMIN_ROLE],
};

/**
 * Check if the current user's roles allow access to a navigation item.
 * Lucid Admin bypasses all role restrictions.
 * @param navigationId - The sidebar navigation ID (kebab-case)
 * @returns true if the user has the required role (or no role restriction exists)
 */
const isNavigationAllowed = (navigationId: string): boolean => {
  if (hasAnyRole([LUCID_ADMIN_ROLE])) return true;
  const requiredRoles = navigationRoleRequirements[navigationId];
  if (!requiredRoles) return true;
  return hasAnyRole(requiredRoles);
};

/**
 * Check if a navigation item is enabled based on its ID (feature flag + role)
 * @param navigationId - The sidebar navigation ID (kebab-case)
 * @returns true if the navigation item is enabled and the user has the required role
 */
export const isNavigationEnabled = (navigationId: string): boolean => {
  const pageId = sidebarIdToPageId[navigationId];
  // If no mapping exists, assume it's enabled (e.g., dashboard)
  if (!pageId) {
    return isNavigationAllowed(navigationId);
  }
  return isPageEnabled(pageId) && isNavigationAllowed(navigationId);
};
